import { useState } from 'react'
import { ScoopText, ScoopButton, ScoopTable, ScoopTab, useToast } from '../../../ui'
import { TrashIcon } from '@phosphor-icons/react'
import { useAccount } from '../../../../api/hooks/account/useAccount'
import { useInvitations } from '../../../../api/hooks/account/useInvitations'
import { useUserInfo } from '../../../../api/hooks/user/useUserInfo'
import { useModal, MODALS } from '../../../modals'
import styles from './TeamSettings.module.scss'

export const TeamSettings = () => {
    const toast = useToast()
    const [selectedTab, setSelectedTab] = useState('members')
    const { user } = useUserInfo()
    const deleteModal = useModal(MODALS.COMMON.DELETE)
    const inviteModal = useModal(MODALS.ACCOUNT.INVITE_MEMBER)
    const accountID = user?.accountID
    const userRole = user?.accountRole || 'member'
    const isAdminOrOwner = ['admin', 'owner'].includes(userRole.toLowerCase())

    // Get account members from useAccount hook
    const { accountMembers, loadingAccountMembers } = useAccount(null, {
        includeAccountMembers: true,
        accountID,
    })

    // Get invitations from useInvitations hook
    const {
        invitations,
        loadingInvitations,
        refetchInvitations,
        cancelInvitation,
        isCancellingInvitation,
    } = useInvitations({
        accountID,
    })

    const teamTabs = [
        { key: 'members', label: 'Members' },
        { key: 'invitations', label: 'Pending Invitations', badgeCount: invitations.length },
    ]

    const handleCancelInvitation = async (invitation) => {
        const confirmed = await deleteModal.show({
            title: 'Invitation',
            description: `Are you sure you want to cancel the invitation for ${invitation.invitedEmail}?`,
        })

        if (confirmed) {
            cancelInvitation(
                { invitationID: invitation.invitationID },
                {
                    onSuccess: () => {
                        refetchInvitations()
                        toast.success('Invitation cancelled successfully')
                    },
                    onError: (error) => {
                        toast.error(error?.message || 'Failed to cancel invitation')
                    },
                }
            )
        }
    }

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A'
        const date = new Date(timestamp)
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }

    const getTimeRemaining = (expiresAt) => {
        if (!expiresAt) return 'N/A'
        const now = Date.now()
        const expires = new Date(expiresAt).getTime()
        const daysRemaining = Math.ceil((expires - now) / (1000 * 60 * 60 * 24))

        if (daysRemaining < 0) return 'Expired'
        if (daysRemaining === 0) return 'Today'
        if (daysRemaining === 1) return '1 day'
        return `${daysRemaining} days`
    }

    const renderTabContent = () => {
        switch (selectedTab) {
            case 'members':
                return (
                    <div className={styles.tableContainer}>
                        <ScoopTable
                            columns={[
                                {
                                    field: 'name',
                                    header: 'Name',
                                    flex: 1,
                                    render: ({ row }) => (
                                        <ScoopText>{row.userName || row.name}</ScoopText>
                                    ),
                                },
                                {
                                    field: 'email',
                                    header: 'Email',
                                    flex: 1,
                                    render: ({ row }) => <ScoopText>{row.email}</ScoopText>,
                                },
                                {
                                    field: 'accountRole',
                                    header: 'Account Role',
                                    flex: 1,
                                    render: ({ row }) => (
                                        <ScoopText>{row.accountRole || 'member'}</ScoopText>
                                    ),
                                },
                            ]}
                            rows={accountMembers}
                            loading={loadingAccountMembers}
                            emptyMessage="No team members found"
                        />
                    </div>
                )
            case 'invitations':
                return (
                    <div className={styles.tableContainer}>
                        <ScoopTable
                            columns={[
                                {
                                    field: 'invitedEmail',
                                    header: 'Email',
                                    flex: 1,
                                    render: ({ row }) => <ScoopText>{row.invitedEmail}</ScoopText>,
                                },
                                {
                                    field: 'role',
                                    header: 'Role',
                                    width: 100,
                                    render: ({ row }) => <ScoopText>{row.role}</ScoopText>,
                                },
                                {
                                    field: 'invitedDate',
                                    header: 'Invited',
                                    width: 120,
                                    render: ({ row }) => (
                                        <ScoopText>{formatDate(row.invitedDate)}</ScoopText>
                                    ),
                                },
                                {
                                    field: 'expiresDate',
                                    header: 'Expires',
                                    width: 100,
                                    render: ({ row }) => (
                                        <ScoopText>{getTimeRemaining(row.expiresDate)}</ScoopText>
                                    ),
                                },
                                {
                                    field: 'actions',
                                    header: '',
                                    width: 60,
                                    sortable: false,
                                    render: ({ row }) =>
                                        isAdminOrOwner && (
                                            <ScoopButton
                                                variant="icon"
                                                className={styles.deleteButton}
                                                onClick={() => handleCancelInvitation(row)}
                                                disabled={isCancellingInvitation}
                                            >
                                                <TrashIcon size={16} weight="regular" />
                                            </ScoopButton>
                                        ),
                                },
                            ]}
                            rows={invitations}
                            loading={loadingInvitations}
                            emptyMessage="No pending invitations"
                        />
                    </div>
                )
            default:
                return null
        }
    }

    return (
        <div className={styles.container}>
            {/* Tabs and Invite Button Row */}
            <div className={styles.topBar}>
                <div className={styles.tabsContainer}>
                    <ScoopTab
                        tabs={teamTabs}
                        value={selectedTab}
                        onChange={(e, newValue) => setSelectedTab(newValue)}
                    />
                </div>
                {isAdminOrOwner && (
                    <div className={styles.inviteButtonContainer}>
                        <ScoopButton
                            onClick={() =>
                                inviteModal.show({
                                    accountID,
                                    onSuccess: refetchInvitations,
                                })
                            }
                            variant="contained"
                        >
                            + Invite Member
                        </ScoopButton>
                    </div>
                )}
            </div>

            {/* Tab Content */}
            <div className={styles.tabContent}>{renderTabContent()}</div>
        </div>
    )
}
